import {configureStore} from '@reduxjs/toolkit';
import {setupListeners} from '@reduxjs/toolkit/query';
import {apiSlice} from 'api/apiSlice';
import {rtkQueryErrorMiddleware} from 'middleware/errorMiddleware';
import {combineReducers} from 'redux';
import {persistReducer, persistStore} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {authSlice} from 'store/authSlice';
import {chatSlice} from 'store/chatSlice';
import {esoySlice} from 'store/esoySlice';
import {formSlice} from 'store/formSlice';
import {googleAuthSlice} from 'store/googleAuthSlice';
import listenerMiddleware from 'store/listeners';
import {unsavedWorkSlice} from 'store/unsavedWorkSlice';

// Combine reducers for persistence
const rootReducer = combineReducers({
  [apiSlice.reducerPath]: apiSlice.reducer,
  auth: authSlice.reducer,
  chat: chatSlice.reducer,
  esoy: esoySlice.reducer,
  form: formSlice.reducer,
  googleAuth: googleAuthSlice.reducer,
  unsavedWork: unsavedWorkSlice.reducer,
});

// Redux Persist configuration
const persistConfig = {
  key: 'root', // the key for the persist state in storage
  storage, // the storage engine to use (localStorage in this case)
  whitelist: ['auth', 'esoy'],
};

// Create a persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure the store with the persisted reducer
export const setupStore = (preloadedState) => {
  const store = configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
        // Significantly reduce Redux internal checks
        thunk: {
          extraArgument: {
            // Add a flag to identify production mode
            isProduction: process.env.NODE_ENV === 'production',
          },
        },
      })
        .prepend(listenerMiddleware.middleware)
        .concat(apiSlice.middleware, rtkQueryErrorMiddleware),
    devTools:
      process.env.NODE_ENV === 'production'
        ? false
        : {
            // Completely disable state tracking in Redux DevTools for tables
            // This is the most effective way to reduce memory usage
            stateSanitizer: (state) => {
              if (!state) return state;

              // Create a clean copy without table data
              const sanitized = {...state};

              // Remove table state completely from DevTools
              if (sanitized.table) {
                sanitized.table = {
                  __NOTE__: 'Table state removed to reduce memory usage',
                  __TABLES__: Object.keys(state.table || {}),
                };
              }

              return sanitized;
            },

            // Sanitize action payloads as well
            actionSanitizer: (action) => {
              if (!action || !action.type) return action;

              // Filter out table-related actions
              if (action.type.startsWith('table/')) {
                return {
                  type: action.type,
                  payload: '<TABLE_PAYLOAD_REMOVED>',
                };
              }

              return action;
            },
            latency: 20, // Limit the number of actions stored in DevTools
            autoPause: true,
            maxAge: 5, // Significantly reduce the number of tracked actions
          },
  });

  setupListeners(store.dispatch);
  return store;
};

// Create a persistor for the store
export const store = setupStore();
export const persistor = persistStore(store);
